import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Trash2, Sparkles, Loader2, Save, Repeat, AlertTriangle,
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MAX_STEPS = 6;
const HOUR_UNITS = [
  { value: 'hours', label: 'hours' },
  { value: 'days',  label: 'days'  },
];

const cryptoId = () => (
  globalThis.crypto?.randomUUID?.()
  ?? `step-${Math.random().toString(36).slice(2, 10)}`
);

// ---------- Utility: split delayHours into a friendly value/unit pair ------
const splitDelay = (hours) => {
  const h = Math.max(0, Math.min(24 * 30, Number(hours) || 0));
  if (h % 24 === 0 && h >= 24) return { value: h / 24, unit: 'days' };
  return { value: h, unit: 'hours' };
};
const joinDelay = (value, unit) => {
  const v = Math.max(0, Math.min(unit === 'days' ? 30 : 24 * 30, Number(value) || 0));
  return unit === 'days' ? v * 24 : v;
};

// ---------- Single-row step editor ----------------------------------------
const StepRow = ({
  step, index, onChange, onRemove, onGenerateAi, busyAi, canRemove,
}) => {
  const { value: dValue, unit: dUnit } = splitDelay(step.delayHours);

  const updateDelay = (rawValue, rawUnit) => {
    const newHours = joinDelay(rawValue, rawUnit);
    onChange({ ...step, delayHours: newHours });
  };

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
      data-testid={`quote-followup-step-${index}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[rgba(242,107,29,0.1)] text-[var(--cb-orange)] text-xs font-semibold">
            {index + 1}
          </span>
          <span className="text-sm font-medium text-secondary">Follow-up #{index + 1}</span>
        </div>
        {canRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(step.id)}
            className="text-gray-400 hover:text-red-600"
            data-testid={`quote-followup-step-${index}-remove`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Timing */}
      <div className="flex items-end gap-2">
        <div>
          <Label className="text-xs text-gray-600">Send after</Label>
          <Input
            type="number"
            min={0}
            max={dUnit === 'days' ? 30 : 720}
            value={dValue}
            onChange={(e) => updateDelay(e.target.value, dUnit)}
            className="w-24 h-9"
            data-testid={`quote-followup-step-${index}-delay-value`}
          />
        </div>
        <div className="w-32">
          <Label className="text-xs text-gray-600 invisible">Unit</Label>
          <Select
            value={dUnit}
            onValueChange={(v) => updateDelay(dValue, v)}
          >
            <SelectTrigger className="h-9" data-testid={`quote-followup-step-${index}-delay-unit`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOUR_UNITS.map((u) => (
                <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-xs text-gray-500 mb-2">after the quote is sent</span>
      </div>

      {/* Message */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">Message</Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onGenerateAi(step.id, index)}
            disabled={busyAi}
            className="h-7 text-xs text-[var(--cb-orange)] hover:bg-[rgba(242,107,29,0.08)]"
            data-testid={`quote-followup-step-${index}-ai-btn`}
          >
            {busyAi
              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {busyAi ? 'Generating…' : 'Generate with AI'}
          </Button>
        </div>
        <Textarea
          rows={3}
          value={step.message}
          onChange={(e) => onChange({ ...step, message: e.target.value, aiGenerated: false })}
          maxLength={500}
          className="text-sm"
          data-testid={`quote-followup-step-${index}-message`}
        />
        <div className="flex items-center justify-between text-[11px] text-gray-500">
          <span>
            Placeholders: <code className="bg-gray-100 px-1 rounded">{'{firstName}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{service}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{company}'}</code>
          </span>
          <span>{step.message.length}/500</span>
        </div>
      </div>
    </div>
  );
};

// ---------- Main settings card --------------------------------------------
const QuoteFollowupSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [steps,   setSteps]   = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [aiBusyId, setAiBusyId] = useState(null);

  // Load from backend
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken(false);
        const res = await fetch(`${API}/api/settings/quote-followup`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await res.json();
        if (cancelled) return;
        if (res.ok && payload?.settings) {
          const s = payload.settings;
          setEnabled(!!s.enabled);
          setSteps(Array.isArray(s.steps) ? s.steps : []);
          setSavedSnapshot(JSON.stringify(s));
        }
      } catch (err) {
        console.error('[QuoteFollowupSettings] load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Dirty check
  const isDirty = useMemo(() => {
    if (!savedSnapshot) return true;
    try {
      const parsed = JSON.parse(savedSnapshot);
      return JSON.stringify({ enabled: parsed.enabled, steps: parsed.steps || [] })
          !== JSON.stringify({ enabled, steps });
    } catch { return true; }
  }, [savedSnapshot, enabled, steps]);

  // ── step ops ───────────────────────────────────────────────────────────
  const updateStep = (id, next) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? next : s)));
  };
  const removeStep = (id) => {
    setSteps((prev) => prev.filter((s) => s.id !== id));
  };
  const addStep = () => {
    if (steps.length >= MAX_STEPS) return;
    const lastDelay = steps[steps.length - 1]?.delayHours ?? 24;
    setSteps((prev) => [
      ...prev,
      {
        id: cryptoId(),
        delayHours: lastDelay + 48,
        message: 'Hey {firstName}, still happy to help on the {service}. Let us know if you want to move forward or have any questions.',
        aiGenerated: false,
      },
    ]);
  };

  const generateAi = async (stepId, stepIndex) => {
    setAiBusyId(stepId);
    try {
      const token = await getAuth().currentUser?.getIdToken(false);
      const step = steps.find((s) => s.id === stepId);
      const res = await fetch(`${API}/api/settings/quote-followup/preview-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${token}`,
        },
        body: JSON.stringify({
          stepIndex,
          delayHours: step?.delayHours || 24,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (res.ok && payload?.message) {
        updateStep(stepId, { ...step, message: payload.message, aiGenerated: true });
        toast.success('AI message generated');
      } else {
        toast.error(payload?.detail || 'Failed to generate message');
      }
    } catch (err) {
      console.error('[QuoteFollowupSettings] AI gen failed:', err);
      toast.error('Network error — please try again');
    } finally {
      setAiBusyId(null);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const token = await getAuth().currentUser?.getIdToken(false);
      const res = await fetch(`${API}/api/settings/quote-followup`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled, steps }),
      });
      const payload = await res.json().catch(() => ({}));
      if (res.ok && payload?.success) {
        setSavedSnapshot(JSON.stringify(payload.settings));
        setSteps(Array.isArray(payload.settings.steps) ? payload.settings.steps : steps);
        toast.success('Quote follow-up settings saved');
      } else {
        toast.error(payload?.detail || 'Failed to save');
      }
    } catch (err) {
      console.error('[QuoteFollowupSettings] save failed:', err);
      toast.error('Network error — please try again');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </section>
    );
  }

  return (
    <section
      className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5"
      data-testid="quote-followup-card"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Repeat className="w-5 h-5 text-gray-500 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold text-secondary">Quote follow-up auto-fire</h3>
            <p className="text-xs text-gray-500 mt-0.5 max-w-md">
              When you send a quote, KRO will automatically follow up by SMS on your cadence —
              skipping anyone who's already approved.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label htmlFor="quote-followup-enabled" className="text-sm">Enabled</Label>
          <Switch
            id="quote-followup-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="quote-followup-enabled-toggle"
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3" data-testid="quote-followup-steps">
        {steps.map((step, idx) => (
          <StepRow
            key={step.id}
            step={step}
            index={idx}
            onChange={(next) => updateStep(step.id, next)}
            onRemove={removeStep}
            onGenerateAi={generateAi}
            busyAi={aiBusyId === step.id}
            canRemove={steps.length > 1}
          />
        ))}

        {steps.length < MAX_STEPS && (
          <Button
            variant="outline"
            size="sm"
            onClick={addStep}
            className="w-full"
            data-testid="quote-followup-add-step"
          >
            <Plus className="w-4 h-4 mr-1" /> Add another follow-up
          </Button>
        )}
      </div>

      {/* Disabled hint */}
      {!enabled && (
        <div
          className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800"
          data-testid="quote-followup-disabled-hint"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Auto-fire is off. Save your cadence here, then flip the toggle when you're ready —
            nothing will be sent until you do.
          </span>
        </div>
      )}

      {/* Save */}
      <div className="pt-2">
        <Button
          onClick={handleSave}
          disabled={saving || !isDirty || steps.length === 0}
          size="sm"
          data-testid="quote-followup-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Saving…' : 'Save cadence'}
        </Button>
      </div>
    </section>
  );
};

export default QuoteFollowupSettings;
